import React from 'react';
import { Card, Typography, Empty, Skeleton, Alert, Tooltip } from 'antd';
import { BarChartOutlined } from '@ant-design/icons';
import { useResponsive } from '../../hooks/useResponsive';
import { useCurrency } from '../../hooks/useCurrency';
import { useAppSelector } from '../../hooks/redux';
import { useGetDashboardPollingQuery } from '../../services/analyticsApi';
import { useGetRestaurantSettingsQuery } from '../../services/restaurantsApi';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { DASHBOARD_QUERY, DASHBOARD_POLLING_MS } from './dashboardQuery';
import type { HourlySalesDto } from '../../types/analytics';
import { useMoneyPrivacy, MONEY_MASK } from '../../hooks/useMoneyPrivacy';

const { Text } = Typography;

// Redondea hacia arriba al siguiente número "limpio" (1-2-2.5-5-10 × 10^n)
// para que el eje Y no termine en un valor arbitrario.
const niceCeil = (value: number): number => {
  if (value <= 0) return 100;
  const exponent = Math.floor(Math.log10(value));
  const base = Math.pow(10, exponent);
  for (const multiple of [1, 2, 2.5, 5, 10]) {
    if (value <= multiple * base) return multiple * base;
  }
  return 10 * base;
};

const RealTimeCharts: React.FC = () => {
  const { isMobile } = useResponsive();
  const { currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { getLocalDate } = useRestaurantTimeZone();
  const { isHidden, mask } = useMoneyPrivacy();
  const moneyHidden = isHidden('otros');

  const { data: dashboard, error, isLoading } = useGetDashboardPollingQuery(
    {
      restaurantId: currentRestaurant?.id || '',
      query: DASHBOARD_QUERY,
    },
    {
      skip: !currentRestaurant?.id,
      refetchOnMountOrArgChange: true,
      pollingInterval: DASHBOARD_POLLING_MS,
    }
  );

  const { data: settings } = useGetRestaurantSettingsQuery(undefined, {
    skip: !currentRestaurant?.id,
  });

  if (!currentRestaurant?.id) {
    return null;
  }

  if (error) {
    return (
      <Alert
        message="Error al cargar gráficos"
        description="No se pudieron cargar las ventas por hora"
        type="error"
        showIcon
      />
    );
  }

  const hourlySales = dashboard?.financialSummary.hourlySales;
  const currentHour = parseInt(getLocalDate('H'), 10);

  const formatMoney = (value: number) =>
    `${currencySymbol} ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatCompact = (value: number) =>
    value >= 1000 ? `${parseFloat((value / 1000).toFixed(1))}k` : `${value}`;

  let content: React.ReactNode;

  if (isLoading || !hourlySales) {
    content = <Skeleton active paragraph={{ rows: 4 }} />;
  } else {
    const byHour = new Map(hourlySales.map((h) => [h.hour, h]));

    // Horario del restaurante: recorre las horas empezando en la de apertura
    // (soporta horarios que cruzan medianoche) y descarta las horas fuera de
    // horario, salvo que tengan ventas — el dinero nunca se oculta.
    let openHour = 0;
    let closeHour = 24;
    let hasSchedule = false;
    try {
      if (settings?.openTime && settings?.closeTime) {
        openHour = parseInt(settings.openTime.split(':')[0], 10);
        closeHour = parseInt(settings.closeTime.split(':')[0], 10);
        hasSchedule = !isNaN(openHour) && !isNaN(closeHour);
      }
    } catch {
      hasSchedule = false;
    }

    const inSchedule = (hour: number) => {
      if (!hasSchedule) return true;
      if (closeHour < openHour) return hour >= openHour || hour < closeHour;
      return hour >= openHour && hour < closeHour;
    };

    const hourOrder = hasSchedule
      ? Array.from({ length: 24 }, (_, i) => (openHour + i) % 24)
      : Array.from({ length: 24 }, (_, i) => i);

    const visibleHours: HourlySalesDto[] = hourOrder
      .map((hour) => byHour.get(hour) ?? { hour, sales: 0, orderCount: 0, averageOrderValue: 0 })
      .filter((h) => inSchedule(h.hour) || h.sales > 0);

    const peakSales = Math.max(...visibleHours.map((h) => h.sales), 0);

    if (peakSales === 0) {
      content = <Empty description="Aún no hay ventas registradas hoy" style={{ margin: '40px 0' }} />;
    } else {
      const axisMax = niceCeil(peakSales * 1.15);
      const currentIndex = visibleHours.findIndex((h) => h.hour === currentHour);
      const chartHeight = isMobile ? 190 : 230;
      const axisWidth = 48;
      const slotMinWidth = isMobile ? 30 : 40;
      const gridTicks = [axisMax, axisMax / 2, 0];

      content = (
        <div style={{ overflowX: 'auto', touchAction: 'pan-x' }}>
          <div
            style={{
              position: 'relative',
              height: chartHeight,
              minWidth: axisWidth + visibleHours.length * slotMinWidth,
            }}
          >
            {/* Gridlines + eje Y */}
            <div style={{ position: 'absolute', top: 20, bottom: 22, left: axisWidth, right: 0 }}>
              {gridTicks.map((tick) => {
                const top = (1 - tick / axisMax) * 100;
                return (
                  <div
                    key={tick}
                    style={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: `${top}%`,
                      borderTop:
                        tick === 0
                          ? '1px solid var(--border-color-strong)'
                          : '1px solid var(--border-color)',
                    }}
                  >
                    <Text
                      type="secondary"
                      style={{
                        position: 'absolute',
                        left: -axisWidth,
                        top: -8,
                        width: axisWidth - 8,
                        textAlign: 'right',
                        fontSize: 10.5,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {moneyHidden ? MONEY_MASK : `${currencySymbol}${formatCompact(tick)}`}
                    </Text>
                  </div>
                );
              })}
            </div>

            {/* Barras */}
            <div
              style={{
                position: 'absolute',
                top: 20,
                bottom: 22,
                left: axisWidth,
                right: 0,
                display: 'flex',
                alignItems: 'flex-end',
                gap: isMobile ? 4 : 6,
                padding: '0 4px',
              }}
            >
              {visibleHours.map((hourData) => {
                const isCurrent = hourData.hour === currentHour;
                const isPeak = hourData.sales === peakSales;
                const showLabel = hourData.sales > 0 && (isPeak || isCurrent);
                const bar = (
                  <div
                    key={hourData.hour}
                    style={{
                      flex: 1,
                      minWidth: slotMinWidth - (isMobile ? 4 : 6),
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                    }}
                  >
                    {showLabel && (
                      <Text
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                          marginBottom: 2,
                          color: isCurrent ? 'var(--color-primary)' : undefined,
                        }}
                        type={isCurrent ? undefined : 'secondary'}
                      >
                        {moneyHidden ? MONEY_MASK : `${currencySymbol}${formatCompact(hourData.sales)}`}
                      </Text>
                    )}
                    {hourData.sales > 0 && (
                      <div
                        style={{
                          width: '100%',
                          maxWidth: 22,
                          height: `${(hourData.sales / axisMax) * 100}%`,
                          minHeight: 3,
                          backgroundColor: 'var(--color-primary)',
                          borderRadius: '4px 4px 0 0',
                        }}
                      />
                    )}
                  </div>
                );
                return (
                  <Tooltip
                    key={hourData.hour}
                    title={
                      `${hourData.hour.toString().padStart(2, '0')}:00 — ${mask('otros', formatMoney(hourData.sales))} · ` +
                      `${hourData.orderCount} ${hourData.orderCount === 1 ? 'orden' : 'órdenes'}` +
                      (isCurrent ? ' · en curso' : '')
                    }
                  >
                    {bar}
                  </Tooltip>
                );
              })}
            </div>

            {/* Etiquetas de hora */}
            <div
              style={{
                position: 'absolute',
                bottom: 0,
                left: axisWidth,
                right: 0,
                display: 'flex',
                gap: isMobile ? 4 : 6,
                padding: '0 4px',
              }}
            >
              {visibleHours.map((hourData, index) => {
                const isCurrent = hourData.hour === currentHour;
                const isFuture = currentIndex >= 0 && index > currentIndex;
                const showText = !isMobile || index % 2 === 0 || isCurrent;
                return (
                  <Text
                    key={hourData.hour}
                    style={{
                      flex: 1,
                      minWidth: slotMinWidth - (isMobile ? 4 : 6),
                      textAlign: 'center',
                      fontSize: 10.5,
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: isCurrent ? 700 : 400,
                      color: isCurrent ? 'var(--color-primary)' : undefined,
                      opacity: isFuture ? 0.45 : 1,
                    }}
                    type={isCurrent ? undefined : 'secondary'}
                  >
                    {showText ? hourData.hour : ''}
                  </Text>
                );
              })}
            </div>
          </div>
        </div>
      );
    }
  }

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <BarChartOutlined style={{ marginRight: 8, color: 'var(--color-primary)' }} />
          <span>Ventas por hora</span>
        </div>
      }
      extra={<Text type="secondary">Hoy</Text>}
      style={{ borderRadius: isMobile ? 8 : 12, height: '100%' }}
    >
      {content}
    </Card>
  );
};

export default RealTimeCharts;
